import { AppointmentListItem } from '@spike/appointments-list-model';
import { Option } from '@spike/model';
import MasterData, { WorkflowStep, WorkflowStepAction } from '@spike/masterdata-model';
import { Moment } from 'moment-timezone';
import {
    AppointmentTransitionContext,
    appendMissingClosedTransitionActions,
    filterAllowedStatusActions
} from 'utils/appointmentTransitionPolicy';

/**
 * Resolves the workflow (ordered list of statuses + allowed actions) that applies to a given
 * appointment, mirroring the resolution used by the individual `StatusButton`.
 */
export const getAppointmentWorkflow = (masterData: MasterData, app: AppointmentListItem): Array<WorkflowStep> => {
    switch (app.service.businessArea) {
        case 'daycare':
            return masterData.workflows.daycare;
        case 'boarding':
            return masterData.workflows.boarding;
        case 'transportation':
            return masterData.workflows.transportation;
        case 'training':
            if (app.training?.type === 'in_home') {
                return masterData.workflows.training.inHome;
            } else if (app.training?.type === 'brick_and_mortar') {
                return masterData.workflows.training.brickAndMortar;
            }
            return [];
        case 'walking':
            if (app.walking?.type === 'in_home') {
                return masterData.workflows.walking.inHome;
            } else if (app.walking?.type === 'brick_and_mortar') {
                return masterData.workflows.walking.brickAndMortar;
            }
            return [];
        default:
            return masterData.workflows.grooming;
    }
};

export interface CommonTransitionContext {
    isOwner: boolean;
    reopenClosedBookings: boolean;
    now: Moment;
}

/** Returns the actions available for a single appointment, applying the same rules used individually. */
export const getAppointmentAvailableActions = (
    app: AppointmentListItem,
    masterData: MasterData,
    context: CommonTransitionContext
): Array<WorkflowStepAction> => {
    const workflow = getAppointmentWorkflow(masterData, app);
    const step = workflow.find(step => step.status.id === app.status.id);
    const workflowActions = step?.actions.filter(action => action.show) ?? [];

    const transitionContext: AppointmentTransitionContext = {
        currentStatusId: app.status.id,
        isOwner: context.isOwner,
        reopenClosedBookings: context.reopenClosedBookings,
        closedAt: app.closedAt ?? undefined,
        now: context.now
    };

    const filtered = filterAllowedStatusActions(workflowActions, transitionContext);
    return appendMissingClosedTransitionActions(filtered, transitionContext, masterData.status);
};

/**
 * Intersects the available actions of every selected appointment, keeping only the transitions
 * (by target status id) that ALL selected appointments can perform.
 */
export const getCommonAvailableActions = (
    apps: Array<AppointmentListItem>,
    masterData: MasterData,
    context: CommonTransitionContext
): Array<WorkflowStepAction> => {
    if (apps.length === 0) {
        return [];
    }

    const actionsByAppointment = apps.map(app => getAppointmentAvailableActions(app, masterData, context));

    const [firstActions, ...restActions] = actionsByAppointment;

    return firstActions.filter(action =>
        restActions.every(actions => actions.some(other => other.nextStatus.id === action.nextStatus.id))
    );
};

/** Unique current status names of the selected appointments, in first-seen order (no duplicates). */
export const getCurrentStatusLabels = (apps: Array<AppointmentListItem>): Array<string> => {
    const labels: Array<string> = [];
    const seenIds = new Set<number>();

    apps.forEach(app => {
        if (!seenIds.has(app.status.id)) {
            seenIds.add(app.status.id);
            labels.push(app.status.name);
        }
    });

    return labels;
};

export const getStatusOption = (statusOptions: Array<Option<number>>, statusId: number): Option<number> | undefined =>
    statusOptions.find(status => status.id === statusId);
